import React from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import {
  DASHBOARD_TOOL_ACCESS_REASONS,
  findProjectNavigationToolByPath,
  resolveProjectManagementEntryRedirect,
  resolveProjectNavigation,
} from "../../utils/dashboardNavigation";
import { isSuperAdmin } from "../../utils/roles";
import DashboardLoadingState from "./DashboardLoadingState";

const AUTHORIZATION_DENIAL_REASONS = new Set([
  DASHBOARD_TOOL_ACCESS_REASONS.PERMISSION,
  DASHBOARD_TOOL_ACCESS_REASONS.ROLE,
]);

function projectMatches(project, key) {
  if (!project || !key) return false;
  return [project.slug, project.id, project._id]
    .filter(Boolean)
    .some((value) => String(value) === String(key));
}

export default function ProjectToolAccessGate({
  children,
  toolKey = "",
  fallback = "/dashboard",
}) {
  const { projectId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const {
    projects,
    activeProject,
    loading,
    loadProjects,
  } = useProjects();
  const [projectLookupKey, setProjectLookupKey] = React.useState("");

  const project = React.useMemo(() => {
    const matched = projects.find((item) => projectMatches(item, projectId));
    return matched || (projectMatches(activeProject, projectId) ? activeProject : null);
  }, [projects, activeProject, projectId]);

  React.useEffect(() => {
    let cancelled = false;
    if (!projectId) return () => { cancelled = true; };
    if (project) {
      setProjectLookupKey(String(projectId));
      return () => { cancelled = true; };
    }
    if (loading || projectLookupKey === String(projectId)) return () => { cancelled = true; };

    Promise.resolve(loadProjects()).finally(() => {
      if (!cancelled) setProjectLookupKey(String(projectId));
    });
    return () => { cancelled = true; };
  }, [projectId, project, loading, projectLookupKey, loadProjects]);

  if (!project) {
    // No montamos una pantalla de proyecto antes de resolver su membresía: evita
    // que una URL directa dispare consultas o acciones de una herramienta sin acceso.
    if (loading || projectLookupKey !== String(projectId || "")) {
      return <DashboardLoadingState label="Preparando el proyecto…" minHeight={360} />;
    }
    return <Navigate to={fallback} replace />;
  }

  const navigation = resolveProjectNavigation({
    project,
    memberAccess: project.memberAccess,
    superAdmin: isSuperAdmin(user),
    pathname: location.pathname,
    search: location.search,
  });
  const managementEntry = resolveProjectManagementEntryRedirect(navigation, location.pathname);
  if (managementEntry) return <Navigate to={managementEntry} replace />;

  const tool = toolKey
    ? navigation.toolsByKey[toolKey] || null
    : findProjectNavigationToolByPath(navigation, location.pathname, location.search);

  if (
    tool?.authorizationResolved &&
    tool.authorized === false &&
    AUTHORIZATION_DENIAL_REASONS.has(tool.accessReason)
  ) {
    return <Navigate to={fallback} replace />;
  }

  if (
    tool?.locked &&
    tool.accessReason === DASHBOARD_TOOL_ACCESS_REASONS.BILLING &&
    navigation.billingAccess?.reason === "billing_suspended"
  ) {
    return (
      <Navigate
        to={`${navigation.projectBase}/configuracion/suscripcion`}
        replace
        state={{ blockedTool: tool.key || "", from: location.pathname }}
      />
    );
  }

  return children;
}
